import { ChangeDetectionStrategy, Component, DestroyRef, effect, input, output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import { THEME_COLORS_WITH_SHADES } from '../../elements/theme';
import type {
  WidgetControl,
  WidgetSliderControl,
  WidgetSliderVariant,
  WidgetButtonControl,
  WidgetCanvasSelectionControl,
  WidgetDataTableControl,
} from '../../../../model/widget-control.model';
import { WIDGET_CONTROL_TYPE } from '../../../../model/widget-control.model';
import type { ActionDefinition } from '../../../../model/widget-action.model';
import { TriggerEditorComponent } from './trigger-editor.component';

interface SelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'dojo-control-fields-editor',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatSlideToggleModule,
    NumberInputComponent,
    TextInputComponent,
    SingleSelectComponent,
    TriggerEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-1.5">
      <div class="grid grid-cols-2 gap-1.5">
        <dojo-text-input label="Label" [formCtrl]="getTextCtrl('label')" [size]="'s'" />
        <dojo-single-select
          label="Position"
          [formCtrl]="getSelectCtrl('position')"
          [options]="control().type === 'data-table' ? tablePositionOptions : positionOptions"
          optionLabel="label"
          optionValue="value"
          [size]="'s'"
          placeholder="Default"
          (selectionChange)="onFieldChange('position', $event)"
        />
      </div>

      @if (control().type === 'slider') {
        <dojo-text-input label="Param" [formCtrl]="getTextCtrl('param')" [size]="'s'" placeholder="f.a" />
        <div class="grid grid-cols-3 gap-1.5">
          <dojo-number-input label="Min" [formCtrl]="getNumberCtrl('rangeMin')" [step]="1" [size]="'s'" />
          <dojo-number-input label="Max" [formCtrl]="getNumberCtrl('rangeMax')" [step]="1" [size]="'s'" />
          <dojo-number-input
            label="Step"
            [formCtrl]="getNumberCtrl('rangeStep')"
            [step]="0.1"
            [min]="0.01"
            [size]="'s'"
          />
        </div>
        <div class="grid grid-cols-2 gap-1.5">
          <dojo-number-input label="Default" [formCtrl]="getNumberCtrl('defaultValue')" [step]="0.1" [size]="'s'" />
          <dojo-single-select
            label="Color"
            [formCtrl]="getSelectCtrl('color')"
            [options]="colorOptions"
            optionLabel="label"
            optionValue="value"
            [size]="'s'"
            placeholder="Default"
            (selectionChange)="onFieldChange('color', $event)"
          />
        </div>
        <div class="grid grid-cols-2 gap-1.5">
          <dojo-single-select
            label="Variant"
            [formCtrl]="getSelectCtrl('variant')"
            [options]="variantOptions"
            optionLabel="label"
            optionValue="value"
            [size]="'s'"
            placeholder="Default"
            (selectionChange)="onFieldChange('variant', $event)"
          />
          <mat-slide-toggle
            class="self-center"
            [checked]="isSliderEditable()"
            (change)="onFieldChange('editable', $event.checked)"
            >Editable</mat-slide-toggle
          >
        </div>
      }

      @if (control().type === 'button') {
        <div class="grid grid-cols-2 gap-1.5">
          <dojo-text-input
            label="Icon"
            [formCtrl]="getTextCtrl('icon')"
            [size]="'s'"
            placeholder="magnifying-glass-plus"
          />
          <dojo-text-input
            label="Group"
            [formCtrl]="getTextCtrl('group')"
            [size]="'s'"
            placeholder="Optional group name"
          />
        </div>
        <mat-slide-toggle [checked]="isButtonShowLabel()" (change)="onFieldChange('showLabel', $event.checked)"
          >Show Label</mat-slide-toggle
        >

        <dojo-trigger-editor
          [trigger]="getButtonTrigger()"
          [elementIds]="elementIds()"
          (triggerChange)="onTriggerChange($event)"
        />
      }

      @if (control().type === 'canvas-selection') {
        <span class="text-xs text-neutral-500">Location</span>
        <div class="grid grid-cols-2 gap-1.5">
          <dojo-text-input
            label="X"
            [formCtrl]="getTextCtrl('locationX')"
            [size]="'s'"
            placeholder="0 or t1.point.A.x"
          />
          <dojo-text-input
            label="Y"
            [formCtrl]="getTextCtrl('locationY')"
            [size]="'s'"
            placeholder="0 or t1.point.A.y"
          />
        </div>
        <dojo-text-input
          label="Group"
          [formCtrl]="getTextCtrl('group')"
          [size]="'s'"
          placeholder="Optional group name"
        />
        <mat-slide-toggle [checked]="isCanvasSelectionSelected()" (change)="onFieldChange('selected', $event.checked)"
          >Initially Selected</mat-slide-toggle
        >

        <span class="text-xs text-neutral-500">Trigger (selected)</span>
        <dojo-trigger-editor
          [trigger]="getCanvasSelectionTrigger()"
          [elementIds]="elementIds()"
          (triggerChange)="onTriggerChange($event)"
        />

        <span class="text-xs text-neutral-500">Trigger (deselected)</span>
        <dojo-trigger-editor
          [trigger]="getCanvasSelectionTriggerOff()"
          [elementIds]="elementIds()"
          (triggerChange)="onTriggerOffChange($event)"
        />
      }
    </div>
  `,
})
export class ControlFieldsEditorComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    effect(() => {
      this.control();
      this.syncFromControl();
    });
  }

  readonly control = input.required<WidgetControl>();
  readonly elementIds = input<string[]>([]);
  readonly controlChange = output<WidgetControl>();

  // --- Lazy form controls ---
  private readonly textCtrls = new Map<string, FormControl<string>>();
  private readonly numberCtrls = new Map<string, FormControl<number | null>>();
  private readonly selectCtrls = new Map<string, FormControl<string>>();

  // --- Options ---
  readonly positionOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    { label: 'Top Left', value: 'top-left' },
    { label: 'Top', value: 'top' },
    { label: 'Top Right', value: 'top-right' },
    { label: 'Bottom Left', value: 'bottom-left' },
    { label: 'Bottom', value: 'bottom' },
    { label: 'Bottom Right', value: 'bottom-right' },
    { label: 'Slider Footer', value: 'slider-footer' },
  ];

  readonly colorOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...THEME_COLORS_WITH_SHADES.map((c) => ({ label: c, value: c })),
  ];

  readonly variantOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    { label: 'Minimal', value: 'minimal' },
  ];

  readonly tablePositionOptions: SelectOption[] = [
    { label: 'Right', value: 'right' },
    { label: 'Right Center', value: 'right-center' },
    { label: 'Right Bottom', value: 'right-bottom' },
    { label: 'Left', value: 'left' },
    { label: 'Left Center', value: 'left-center' },
    { label: 'Left Bottom', value: 'left-bottom' },
    { label: 'Top', value: 'top' },
    { label: 'Top Center', value: 'top-center' },
    { label: 'Top Right', value: 'top-right' },
    { label: 'Bottom', value: 'bottom' },
    { label: 'Bottom Center', value: 'bottom-center' },
    { label: 'Bottom Right', value: 'bottom-right' },
  ];

  // --- Form control getters ---

  getTextCtrl(field: string): FormControl<string> {
    if (!this.textCtrls.has(field)) {
      const ctrl = new FormControl<string>('', { nonNullable: true });
      this.textCtrls.set(field, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => this.onFieldChange(field, val)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.textCtrls.get(field)!;
  }

  getNumberCtrl(field: string): FormControl<number | null> {
    if (!this.numberCtrls.has(field)) {
      const ctrl = new FormControl<number | null>(null);
      this.numberCtrls.set(field, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => this.onFieldChange(field, val)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.numberCtrls.get(field)!;
  }

  getSelectCtrl(field: string): FormControl<string> {
    if (!this.selectCtrls.has(field)) {
      this.selectCtrls.set(field, new FormControl<string>('', { nonNullable: true }));
    }
    return this.selectCtrls.get(field)!;
  }

  isSliderEditable(): boolean {
    const c = this.control();
    return c.type === WIDGET_CONTROL_TYPE.Slider && (c as WidgetSliderControl).editable === true;
  }

  isButtonShowLabel(): boolean {
    const c = this.control();
    return c.type === WIDGET_CONTROL_TYPE.Button && (c as WidgetButtonControl).showLabel !== false;
  }

  getButtonTrigger(): ActionDefinition {
    const c = this.control();
    if (c.type === WIDGET_CONTROL_TYPE.Button) {
      return (c as WidgetButtonControl).trigger ?? { action: 'PATCH_DISPLAY' };
    }
    return { action: 'PATCH_DISPLAY' };
  }

  isCanvasSelectionSelected(): boolean {
    const c = this.control();
    return c.type === WIDGET_CONTROL_TYPE.CanvasSelection && (c as WidgetCanvasSelectionControl).selected === true;
  }

  getCanvasSelectionTrigger(): ActionDefinition {
    const c = this.control();
    if (c.type === WIDGET_CONTROL_TYPE.CanvasSelection) {
      return (c as WidgetCanvasSelectionControl).trigger ?? { action: 'PATCH_DISPLAY' };
    }
    return { action: 'PATCH_DISPLAY' };
  }

  getCanvasSelectionTriggerOff(): ActionDefinition {
    const c = this.control();
    if (c.type === WIDGET_CONTROL_TYPE.CanvasSelection) {
      return (c as WidgetCanvasSelectionControl).triggerOff ?? { action: 'PATCH_DISPLAY' };
    }
    return { action: 'PATCH_DISPLAY' };
  }

  onTriggerChange(trigger: ActionDefinition): void {
    const updated = { ...this.control(), trigger } as WidgetControl;
    this.controlChange.emit(updated);
  }

  onTriggerOffChange(triggerOff: ActionDefinition): void {
    const updated = { ...this.control(), triggerOff } as WidgetControl;
    this.controlChange.emit(updated);
  }

  onFieldChange(field: string, value: unknown): void {
    const updated = { ...this.control() } as Record<string, unknown>;

    if (field === 'rangeMin' || field === 'rangeMax' || field === 'rangeStep') {
      const slider = updated as unknown as WidgetSliderControl;
      const range: [number, number, number?] = [...(slider.range ?? [0, 10])];
      if (field === 'rangeMin') {
        range[0] = (value as number) ?? 0;
      }
      if (field === 'rangeMax') {
        range[1] = (value as number) ?? 10;
      }
      if (field === 'rangeStep') {
        range[2] = (value as number) ?? undefined;
      }
      updated['range'] = range;
    } else if (field === 'locationX' || field === 'locationY') {
      const cs = updated as unknown as WidgetCanvasSelectionControl;
      const loc = { ...cs.location };
      if (field === 'locationX') {
        loc.x = this.parseLocationValue((value as string) ?? '0');
      }
      if (field === 'locationY') {
        loc.y = this.parseLocationValue((value as string) ?? '0');
      }
      updated['location'] = loc;
    } else if (field === 'position' || field === 'color' || field === 'group') {
      updated[field] = (value as string) || undefined;
    } else if (field === 'variant') {
      updated[field] = ((value as string) || undefined) as WidgetSliderVariant | undefined;
    } else {
      updated[field] = value;
    }

    this.controlChange.emit(updated as unknown as WidgetControl);
  }

  // --- Private ---

  private parseLocationValue(raw: string): number | string {
    const trimmed = raw.trim();
    const num = Number(trimmed);
    return !isNaN(num) && trimmed !== '' ? num : trimmed;
  }

  private syncFromControl(): void {
    const control = this.control();
    this.getTextCtrl('label').setValue(control.label ?? '', { emitEvent: false });
    this.getSelectCtrl('position').setValue((control.position as string) ?? '', { emitEvent: false });

    if (control.type === WIDGET_CONTROL_TYPE.Slider) {
      const slider = control as WidgetSliderControl;
      this.getTextCtrl('param').setValue(slider.param ?? '', { emitEvent: false });
      this.getNumberCtrl('rangeMin').setValue(slider.range?.[0] ?? 0, { emitEvent: false });
      this.getNumberCtrl('rangeMax').setValue(slider.range?.[1] ?? 10, { emitEvent: false });
      this.getNumberCtrl('rangeStep').setValue(slider.range?.[2] ?? null, { emitEvent: false });
      this.getNumberCtrl('defaultValue').setValue(slider.defaultValue ?? null, { emitEvent: false });
      this.getSelectCtrl('color').setValue((slider.color as string) ?? '', { emitEvent: false });
      this.getSelectCtrl('variant').setValue((slider.variant as string) ?? '', { emitEvent: false });
    } else if (control.type === WIDGET_CONTROL_TYPE.Button) {
      const button = control as WidgetButtonControl;
      this.getTextCtrl('icon').setValue(button.icon ?? '', { emitEvent: false });
      this.getTextCtrl('group').setValue(button.group ?? '', { emitEvent: false });
    } else if (control.type === WIDGET_CONTROL_TYPE.DataTable) {
      const table = control as WidgetDataTableControl;
      this.getSelectCtrl('position').setValue((table.position as string) ?? 'right', { emitEvent: false });
    } else if (control.type === WIDGET_CONTROL_TYPE.CanvasSelection) {
      const cs = control as WidgetCanvasSelectionControl;
      this.getTextCtrl('locationX').setValue(String(cs.location.x ?? 0), { emitEvent: false });
      this.getTextCtrl('locationY').setValue(String(cs.location.y ?? 0), { emitEvent: false });
      this.getTextCtrl('group').setValue(cs.group ?? '', { emitEvent: false });
    }
  }
}
